/**
 * LYNKSTAR API Service
 * Handles all HTTP API calls to the backend
 */

const API_URL = import.meta.env.VITE_API_URL || 'https://api.lynkstar.app/api/v1'

// Types
export type MessageStatus = 'sending' | 'sent' | 'delivered' | 'read' | 'failed'

export interface User {
  id: string
  username: string
  avatar?: string
  publicKey?: string
  isOnline?: boolean
}

export interface Message {
  id: string
  senderId: string
  text: string
  timestamp: string
  status: MessageStatus
  ciphertext?: string
}

export interface Conversation {
  recipientId: string
  recipientName?: string
  recipientAvatar?: string
  isOnline: boolean
  lastMessage?: Message
  unreadCount: number
}

export interface PreKeyBundle {
  identityKey: string
  signedPreKey: string
  signedPreKeySignature: string
  signedPreKeyId: number
  oneTimePreKey?: string
  oneTimePreKeyId?: number
}

export interface AuthResponse {
  token: string
  user: User
}

export interface Group {
  id: string
  name: string
  avatar_url?: string
  creator_id: string
  created_at: string
  member_count: number
  role?: string
  my_role?: string
}

export interface GroupMember {
  id: string
  username: string
  avatar_url?: string
  role: string
  joined_at: string
}

export interface GroupMessage {
  id: string
  senderId: string
  senderName?: string
  ciphertext: string
  timestamp: string
  text?: string
}

export interface ApiError extends Error {
  status?: number
  code?: string
}

// Helper function for API requests
async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = localStorage.getItem('lynkstar_token')

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {}),
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers,
  })

  if (!response.ok) {
    const error: ApiError = new Error('API request failed')
    error.status = response.status

    try {
      const data = await response.json()
      error.message = data.message || data.error || 'Request failed'
      error.code = data.code
    } catch {
      error.message = `HTTP ${response.status}: ${response.statusText}`
    }

    throw error
  }

  // Handle empty responses
  const text = await response.text()
  if (!text) return {} as T

  return JSON.parse(text) as T
}

// API methods
export const api = {
  // Authentication
  async login(username: string): Promise<AuthResponse> {
    return request<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username }),
    })
  },

  async register(
    username: string,
    keys: {
      identityKey: string
      signedPreKey: { id: number; publicKey: string; signature: string }
      oneTimePreKeys: { id: number; publicKey: string }[]
    }
  ): Promise<AuthResponse> {
    return request<AuthResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({
        username,
        identity_key: keys.identityKey,
        signed_pre_key: keys.signedPreKey.publicKey,
        signed_pre_key_sig: keys.signedPreKey.signature,
        one_time_pre_keys: keys.oneTimePreKeys.map(k => k.publicKey),
      }),
    })
  },

  async logout(): Promise<void> {
    try {
      await request('/auth/logout', { method: 'POST' })
    } catch {
      // Ignore logout errors
    }
  },

  // User
  async getCurrentUser(): Promise<User> {
    return request<User>('/user/me')
  },

  async getUserByUsername(username: string): Promise<User> {
    return request<User>(`/user/${encodeURIComponent(username)}`)
  },

  async searchUsers(query: string): Promise<User[]> {
    return request<User[]>(`/user/search?q=${encodeURIComponent(query)}`)
  },

  // Pre-keys for Signal Protocol
  async uploadPreKeys(
    signedPreKey: { id: number; publicKey: string; signature: string },
    oneTimePreKeys: { id: number; publicKey: string }[]
  ): Promise<void> {
    await request('/keys/upload', {
      method: 'POST',
      body: JSON.stringify({
        signed_pre_key: signedPreKey,
        one_time_pre_keys: oneTimePreKeys,
      }),
    })
  },

  async getPreKeyBundle(userId: string): Promise<PreKeyBundle> {
    return request<PreKeyBundle>(`/keys/${encodeURIComponent(userId)}`)
  },

  // Conversations
  async getConversations(): Promise<Conversation[]> {
    return request<Conversation[]>('/conversations')
  },

  async deleteConversation(recipientId: string): Promise<void> {
    await request(`/conversations/${encodeURIComponent(recipientId)}`, {
      method: 'DELETE',
    })
  },

  // Messages
  async getMessages(recipientId: string, limit = 50, before?: string): Promise<Message[]> {
    const params = new URLSearchParams({ limit: limit.toString() })
    if (before) params.append('before', before)

    return request<Message[]>(
      `/messages/${encodeURIComponent(recipientId)}?${params}`
    )
  },

  async sendMessage(recipientId: string, ciphertext: string): Promise<Message> {
    return request<Message>(`/messages/${encodeURIComponent(recipientId)}`, {
      method: 'POST',
      body: JSON.stringify({ ciphertext }),
    })
  },

  async markAsRead(recipientId: string, messageIds: string[]): Promise<void> {
    await request(`/messages/${encodeURIComponent(recipientId)}/read`, {
      method: 'POST',
      body: JSON.stringify({ message_ids: messageIds }),
    })
  },

  // File upload
  async uploadFile(file: File, recipientId: string): Promise<{ url: string; id: string }> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('recipient_id', recipientId)

    const token = localStorage.getItem('lynkstar_token')
    const response = await fetch(`${API_URL}/files/upload`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    })

    if (!response.ok) {
      throw new Error('File upload failed')
    }

    return response.json()
  },

  // Block/unblock users
  async blockUser(userId: string): Promise<void> {
    await request(`/user/${encodeURIComponent(userId)}/block`, {
      method: 'POST',
    })
  },

  async unblockUser(userId: string): Promise<void> {
    await request(`/user/${encodeURIComponent(userId)}/unblock`, {
      method: 'POST',
    })
  },

  async getBlockedUsers(): Promise<User[]> {
    return request<User[]>('/user/blocked')
  },

  // ============================================
  // Group API
  // ============================================

  async createGroup(name: string, members?: string[], avatarUrl?: string): Promise<Group> {
    return request<Group>('/groups', {
      method: 'POST',
      body: JSON.stringify({ name, members, avatar_url: avatarUrl }),
    })
  },

  async getGroups(): Promise<Group[]> {
    return request<Group[]>('/groups')
  },

  async getGroup(groupId: string): Promise<Group> {
    return request<Group>(`/groups/${encodeURIComponent(groupId)}`)
  },

  async updateGroup(groupId: string, data: { name?: string; avatar_url?: string }): Promise<void> {
    await request(`/groups/${encodeURIComponent(groupId)}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  },

  async deleteGroup(groupId: string): Promise<void> {
    await request(`/groups/${encodeURIComponent(groupId)}`, {
      method: 'DELETE',
    })
  },

  async getGroupMembers(groupId: string): Promise<GroupMember[]> {
    return request<GroupMember[]>(`/groups/${encodeURIComponent(groupId)}/members`)
  },

  async addGroupMembers(groupId: string, memberIds: string[]): Promise<{ added: number }> {
    return request<{ added: number }>(`/groups/${encodeURIComponent(groupId)}/members`, {
      method: 'POST',
      body: JSON.stringify({ members: memberIds }),
    })
  },

  async removeGroupMember(groupId: string, memberId: string): Promise<void> {
    await request(`/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(memberId)}`, {
      method: 'DELETE',
    })
  },

  async leaveGroup(groupId: string): Promise<{ success: boolean; group_deleted?: boolean }> {
    return request<{ success: boolean; group_deleted?: boolean }>(
      `/groups/${encodeURIComponent(groupId)}/leave`,
      { method: 'POST' }
    )
  },

  async getGroupMessages(groupId: string, limit = 50, before?: string): Promise<GroupMessage[]> {
    let url = `/groups/${encodeURIComponent(groupId)}/messages?limit=${limit}`
    if (before) url += `&before=${encodeURIComponent(before)}`
    return request<GroupMessage[]>(url)
  },

  async sendGroupMessage(groupId: string, ciphertext: string): Promise<GroupMessage> {
    return request<GroupMessage>(`/groups/${encodeURIComponent(groupId)}/messages`, {
      method: 'POST',
      body: JSON.stringify({ ciphertext }),
    })
  },
}

export default api
